// Moved from `src/hooks/query/useQuerySessionChats.ts` (now a shim) at
// ARCFE-1003. Fully headless — the only app couplings were the module-scope
// `reactQueryClient` (now the seam's client) and the raw `scribeHttpV2`
// import (now the E5 registry, via `../api/list`).
import { useQuery } from '@tanstack/react-query';

import { GET_SESSION_CHATS, type ChatListResponse } from '../../contracts';
import { getSessionChats } from '../api/list';
import { getSessionDataConfig } from '../config';

export { GET_SESSION_CHATS };

export const invalidateSessionChats = (sessionId: string) => {
  return getSessionDataConfig()
    .getQueryClient()
    .invalidateQueries({
      queryKey: [GET_SESSION_CHATS, sessionId],
    });
};

/**
 * Chats for a specific session (`GET /v2/chat/sessions/:id/chats`).
 *
 * The page/pageSize arguments are part of the CACHE KEY, not just the request
 * — preserved in position so existing cache entries keep resolving.
 *
 * `enabled` is the app-resolved gate, appended so the three positional
 * arguments above keep their places. It is deliberately NOT part of the key:
 * the same session's chats are the same cache entry whether or not this build
 * may ask for them, so a build that can ask reuses what a disabled render left
 * behind instead of minting a second identity.
 */
export function useQuerySessionChats(
  sessionId: string | undefined,
  page = 1,
  pageSize = 20,
  enabled = true
) {
  return useQuery<ChatListResponse>({
    queryKey: [GET_SESSION_CHATS, sessionId, page, pageSize],
    queryFn: async () => {
      const { data } = await getSessionChats(sessionId, page, pageSize);
      return data;
    },
    enabled: enabled && !!sessionId,
  });
}
